import dagre from '@dagrejs/dagre'
import type { AgentEdge, AgentNode } from '@saaga/shared/interfaces/agency-node.interface'

export const NODE_WIDTH = 220
export const NODE_HEIGHT = 80

export type LayoutDirection = 'TB' | 'LR'

interface LayoutedElements {
  nodeList: AgentNode[]
  edgeList: AgentEdge[]
}

/**
 * Applies dagre auto-layout algorithm to position nodes hierarchically.
 * TB = Top to Bottom (vertical hierarchy)
 * LR = Left to Right (horizontal hierarchy)
 */
export const getLayoutedElements = (
  nodeList: AgentNode[],
  edgeList: AgentEdge[],
  direction: LayoutDirection = 'TB'
): LayoutedElements => {
  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
  const isHorizontal = direction === 'LR'

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 50,
    ranksep: 100,
    marginx: 20,
    marginy: 20,
  })

  nodeList.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  })

  edgeList.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target)
  })

  dagre.layout(dagreGraph)

  const layoutedNodeList = nodeList.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id)

    return {
      ...node,
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    } as AgentNode
  })

  return {
    nodeList: layoutedNodeList,
    edgeList,
  }
}

/**
 * Checks if a node has any child nodes (outgoing edges).
 */
export const hasChildNodes = (nodeId: string, edgeList: AgentEdge[]): boolean => {
  return edgeList.some((edge) => edge.source === nodeId)
}

/**
 * Gets the list of child node IDs for a given node.
 */
export const getChildNodeIdList = (nodeId: string, edgeList: AgentEdge[]): string[] => {
  return edgeList.filter((edge) => edge.source === nodeId).map((edge) => edge.target)
}

/**
 * Generates a unique ID for a new node.
 */
export const generateNodeId = (): string => {
  return `agent-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Generates a unique ID for a new edge.
 */
export const generateEdgeId = (source: string, target: string): string => {
  return `edge-${source}-${target}`
}
